import { ProductPriceRangeFragment } from '@graphql/fragments/__generated__/ProductPriceRange.generated'
import PlaceHolder from 'images/placeholder-img.svg'
import React, { MouseEventHandler, useMemo, useRef, useState } from 'react'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import { Link } from 'react-router-dom'
import { getProductUrl } from '../../urls'
import FavoritesBtnView from './FavoritesBtn/FavoritesBtnView'
import ProductDetailsDialog from './ProductDetailsDialog'
import { PromoCodeFragment } from '@graphql/fragments/__generated__/PromoCode.generated'
import FormattedPrice from '@components/FormattedPrice'
import _ from 'lodash'
import { useTheme } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperClass, { SwiperOptions } from 'swiper'
import AddShoppingCartIcon from '@icons/AddShoppingCartIcon'
import ArrowForwardIosIcon from '@icons/ArrowForwardIosIcon'
import CloseIcon from '@icons/CloseIcon'
import IconButton from '@mui/material/IconButton'
import { useTranslation } from 'react-i18next'
import ProductCardPanel, { PatternType } from './ProductCardPanel/ProductCardPanel'
import ProductCardSheet from './ProductCardPanel/ProductCardSheet'
import PanelCollapse from './ProductCardPanel/PanelCollapse'
import { EXTRA_ROW_SPACE } from './ProductCardPanel/common'
import { ReflectionCardFragment } from '@graphql/fragments/__generated__/ReflectionCard.generated'

export const ProductPrice: React.FC<{
  priceRange: ProductPriceRangeFragment
  rangeSeparator?: string
}> = ({ priceRange, rangeSeparator = '-' }) => {
  const price_min = priceRange.discount.min
  const price_max = priceRange.discount.max
  const undiscount_price_min = priceRange.undiscount.min
  const undiscount_price_max = priceRange.undiscount.max
  const currency = priceRange.currency
  const isPriceRange = price_min !== price_max
  const isSale = undiscount_price_min !== price_min || undiscount_price_max !== price_max
  return (
    <p className='price' itemProp='offers' itemType='https://schema.org/Offer'>
      <span className='flex flex-col'>
        <span className={isSale ? 'line-through' : ''}>
          {!isPriceRange && (
            <span className='currency data-hj-allow'>
              <FormattedPrice currency={currency} amount={undiscount_price_min} />
            </span>
          )}
          {isPriceRange && (
            <span className='currency data-hj-allow'>
              <FormattedPrice currency={currency} amount={undiscount_price_min} /> {rangeSeparator}{' '}
              <FormattedPrice currency={currency} amount={undiscount_price_max} />
            </span>
          )}
        </span>
        {isSale && (
          <span className='text-red'>
            {!isPriceRange && (
              <span className='currency data-hj-allow'>
                <FormattedPrice currency={currency} amount={price_min} />
              </span>
            )}
            {isPriceRange && (
              <span className='currency data-hj-allow'>
                <FormattedPrice currency={currency} amount={price_min} /> {rangeSeparator}{' '}
                <FormattedPrice currency={currency} amount={price_max} />
              </span>
            )}
          </span>
        )}
      </span>
    </p>
  )
}

// Below this image width the form doesn't fit inside the card without shrinking the image to almost nothing (the
// scale would drop under ~0.45), so it opens in the bottom sheet instead (the 7-column grid, the filter open, ...).
const MIN_INLINE_PANEL_IMAGE_WIDTH = 220
const MIN_OPEN_IMAGE_SCALE = 0.3
// The panel's negative bottom margin, which overlaps the card's bottom padding (`.product-card__panel` in the SCSS).
const PANEL_BOTTOM_OVERLAP = 12

// Card-view controls, styled through `sx` (MUI's styles are injected after the SCSS). Showing them on hover lives in
// the SCSS (`.product-card__overlay > *`), whose selectors are more specific.
const arrowSx = {
  position: 'absolute',
  top: '50%',
  p: 0,
  color: 'secondary.main',
  transform: 'translateY(-50%)',
  // Keeps the bare chevron readable on dark or busy photos.
  filter: 'drop-shadow(0 0 2px rgba(255, 255, 255, 0.9))',
  '&:hover': { bgcolor: 'transparent', color: '#69c0b2' },
  '& svg': { fontSize: 28 },
} as const

const cartButtonSx = {
  position: 'absolute',
  right: { xs: 6, sm: 8 },
  bottom: { xs: 6, sm: 8 },
  width: { xs: 40, sm: 48 },
  height: { xs: 40, sm: 48 },
  bgcolor: '#69c0b2',
  color: '#fff',
  boxShadow: '0 2px 8px rgba(0, 80, 88, 0.25)',
  '&:hover': { bgcolor: 'secondary.main', transform: 'scale(1.05)' },
  '& svg': { fontSize: { xs: 20, sm: 24 } },
} as const

// The theme sets IconButton's color to `inherit`, so the `color` prop has no effect; the color is set here.
// Positioned against the card: the X glyph (~10px inside the button) lands 16px from the card edges, i.e. 8px from
// the image, like the heart and the cart button.
const closeButtonSx = {
  position: 'absolute',
  color: 'secondary.main',
  top: 6,
  right: 6,
  zIndex: 3,
  width: 36,
  height: 36,
  '& svg': { fontSize: 28 },
} as const

interface ReflectionCardProps {
  spec: ReflectionCardFragment

  onGooglePushDataLayer(spec, name: string, index: number, type: string): void

  showBigImg: boolean
  idx: number
  // Prefix of the card's DOM ids, for a second list of cards on the same page (e.g. Recently Viewed under the grid).
  idPrefix?: string
  showFavoritesButton?: boolean
  // The retail grid no longer fetches the reflection's categories (see reflection.graphql);
  // the current category name is passed down from the page for analytics instead.
  categoryName?: string
  // The redesigned product card (Category and Search pages). Styles live in `.product-card`.
  cardView?: boolean
  // Quick add-to-cart panel of the card view; the list keeps at most one open.
  isPanelOpen?: boolean
  // Wholesale mode: wholesale prices (`product.wholesalePriceRange`) and a panel adding to the wholesale cart.
  wholesale?: boolean

  onTogglePanel?(open: boolean): void
}

const ReflectionCard: React.FC<ReflectionCardProps> = ({
  spec,
  onGooglePushDataLayer,
  showBigImg,
  idx,
  idPrefix = '',
  showFavoritesButton,
  categoryName,
  cardView,
  isPanelOpen = false,
  wholesale = false,
  onTogglePanel,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const lgDown = useMediaQuery(theme.breakpoints.down('lg'))
  // Phones get the quick add-to-cart form in a bottom sheet instead of inside the card.
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'))
  const [activeProductData, setActiveProductData] = useState(null)
  const [activeSlideIndex, setActiveSlideIndex] = useState(0)
  const [swiper, setSwiper] = useState<SwiperClass | null>(null)
  // KIT or PDF picked in the open panel; kept while flipping slides, so the next product gets the same type.
  const [patternType, setPatternType] = useState<PatternType | null>(null)
  // Where the quick add-to-cart form opens: inside the card, or in ProductCardSheet (phones and narrow cards).
  const [narrowCard, setNarrowCard] = useState(false)
  const inSheet = isPhone || narrowCard
  // Heights measured when the form opens inside the card; the image scale (below) is derived from them.
  // `collapsed`: the state of the panel's extra row at that moment (it can start collapsed).
  const [openMeasure, setOpenMeasure] = useState<{
    image: number
    price: number
    panel: number
    collapsed: boolean
  } | null>(null)
  // The panel's empty extra row collapsed (select mode), which frees EXTRA_ROW_SPACE. The ref is read while measuring,
  // in the same layout pass where the row reports its state (before the state update renders).
  const [extraRowCollapsed, setExtraRowCollapsed] = useState(false)
  // The panel inside the card has finished its opening animation (see ExtraRowContext.settled).
  const [panelOpened, setPanelOpened] = useState(false)
  const extraRowCollapsedRef = useRef(false)
  const onExtraRowCollapsedChange = (collapsed: boolean) => {
    extraRowCollapsedRef.current = collapsed
    setExtraRowCollapsed(collapsed)
  }
  // How much the image shrinks while the form is open inside the card: exactly what the panel takes, minus the
  // price that hides, so the card keeps its height.
  const openImageScale = useMemo(() => {
    if (!openMeasure) return null
    if (!openMeasure.image) return 1
    // The panel height changes by EXTRA_ROW_SPACE whenever the row's state differs from the one it was measured in.
    const panel = openMeasure.panel + (Number(openMeasure.collapsed) - Number(extraRowCollapsed)) * EXTRA_ROW_SPACE
    const needed = panel - PANEL_BOTTOM_OVERLAP - openMeasure.price
    return Math.max(MIN_OPEN_IMAGE_SCALE, 1 - needed / openMeasure.image)
  }, [openMeasure, extraRowCollapsed])
  const mediaRef = useRef<HTMLDivElement>(null)
  const sliderRef = useRef<HTMLDivElement>(null)
  const priceRef = useRef<HTMLDivElement>(null)
  const isOpenInCard = cardView && isPanelOpen && !inSheet && openImageScale !== null

  const openPanel = () => {
    setNarrowCard((mediaRef.current?.offsetWidth ?? 0) < MIN_INLINE_PANEL_IMAGE_WIDTH)
    setOpenMeasure(null)
    setExtraRowCollapsed(false)
    setPanelOpened(false)
    setPatternType(null)
    extraRowCollapsedRef.current = false
    onTogglePanel?.(true)
  }

  // Runs before the opening animation starts, so all parts animate from the same frame. `offsetHeight` is the
  // untransformed layout height of the image.
  const onPanelMeasured = (panelHeight: number) => {
    setOpenMeasure({
      image: sliderRef.current?.offsetHeight || 0,
      price: priceRef.current?.scrollHeight || 0,
      panel: panelHeight,
      collapsed: extraRowCollapsedRef.current,
    })
  }
  const product = spec.product
  const priceRange = wholesale ? product?.wholesalePriceRange : product?.priceRange

  const specUrl = getProductUrl(spec.slug, spec.pk)
  const handleClickLink = (event: any, pk: number, slug: string) => {
    event.preventDefault()
    onGooglePushDataLayer(spec, categoryName, idx + 1, 'Category Results')
    setActiveProductData({ pk, slug })
  }

  const slides = !product?.variantImages?.length
    ? [
        {
          image: product?.thumbnail.lg || PlaceHolder,
          smallImage: product?.thumbnail.sm || PlaceHolder,
          link: getProductUrl(spec.slug, spec.pk),
          pk: spec.pk,
          slug: spec.slug,
          name: spec.shortName || spec.name,
          priceRange,
        },
      ]
    : product?.variantImages.map(item => ({
        image: item.thumbnail.lg,
        smallImage: item.thumbnail.sm,
        link: getProductUrl(spec.slug, spec.pk),
        pk: spec.pk,
        slug: spec.slug,
        name: spec.shortName || spec.name,
        priceRange,
      }))

  if (spec.product.similarSpecifics?.length) {
    slides.push(
      ...spec.product.similarSpecifics.map(item => ({
        image: item.thumbnail.lg,
        smallImage: item.thumbnail.sm,
        link: getProductUrl(item.specifics[0]?.slug, item.specifics[0]?.pk),
        pk: item.specifics[0].pk,
        slug: item.specifics[0]?.slug,
        name: item.specifics[0]?.shortName || item.specifics[0]?.name,
        priceRange: item.priceRange,
      }))
    )
  }
  const uniqSlides = _.uniqBy(slides, 'image')
  const activeSlide = uniqSlides[activeSlideIndex]
  // The form is for the product on the current slide (a similar product's slide gets its own options).
  const panelSpecPk = activeSlide?.pk ?? spec.pk

  const swiperOptions: SwiperOptions = useMemo(
    () => ({
      spaceBetween: 0,
      slidesPerView: 1,
      lazy: false,
      preloadImages: true,
      pagination: {
        clickable: true,
        el: `#swiper-pagination-${idPrefix}${idx}`,
      },
      effect: lgDown ? 'slide' : 'fade',
      // The card view flips slides with arrows, endlessly; the old view maps the mouse position to a slide index.
      loop: cardView && uniqSlides.length > 1,
      // The card view flips slides with arrows, so it gets a visible transition; the old view follows the mouse.
      speed: lgDown || cardView ? 250 : 0,
      watchSlidesProgress: true,
      autoHeight: false,
      touchMoveStopPropagation: true,
      preventClicksPropagation: true,
      watchOverflow: false,
    }),
    [JSON.stringify(uniqSlides), idx, idPrefix, lgDown, cardView]
  )

  const handleMouseMove = swiper => e => {
    if (!swiper || lgDown) return

    const rect = swiper.el.getBoundingClientRect()

    const x = e.clientX - rect.left
    const percent = x / rect.width

    const index = Math.max(Math.floor(percent * swiper.slides.length), 0)
    swiper.slideTo(index)
  }

  const handleMouseLeave = swiper => () => {
    if (!swiper || lgDown) return
    swiper.slideTo(0)
  }

  // Keyed by product; mounts when opened, so its data loads on click.
  const panel = (
    <ProductCardPanel
      key={panelSpecPk}
      specPk={panelSpecPk}
      // Known for the card's own product only (a similar product's slide falls back to the default skeleton).
      expectedLayout={
        panelSpecPk === spec.pk ? (wholesale ? product?.wholesaleQuickAddLayout : product?.quickAddLayout) : null
      }
      categoryName={categoryName}
      wholesale={wholesale}
      patternType={patternType}
      onPatternTypeChange={setPatternType}
      onExtraRowCollapsedChange={onExtraRowCollapsedChange}
      // The bottom sheet has no card height to keep.
      settled={inSheet || panelOpened}
      onClose={() => onTogglePanel?.(false)}
    />
  )

  return (
    <>
      <div>
        <div
          ref={mediaRef}
          className={cardView ? `product-card__media ${isOpenInCard ? 'product-card__media--open' : ''}` : ''}
          style={isOpenInCard ? ({ '--product-card-open-scale': openImageScale } as React.CSSProperties) : undefined}
        >
          <div ref={sliderRef} className={cardView ? 'product-card__slider' : ''}>
            <Swiper
              key={lgDown ? 'mobile' : 'desktop'}
              onSwiper={swiper => {
                setSwiper(swiper)
                if (!cardView) {
                  swiper.el.addEventListener('mousemove', handleMouseMove(swiper))
                  swiper.el.addEventListener('mouseleave', handleMouseLeave(swiper))
                }
              }}
              onSlideChange={swiper => {
                setActiveSlideIndex(swiper.realIndex)
              }}
              {...swiperOptions}
            >
              {uniqSlides.map(slide => (
                <SwiperSlide key={slide.image} className='!w-full'>
                  <div
                    className={`relative bg-white ${product?.isOutOfStock ? 'opacity-50' : ''}`}
                    style={{ paddingBottom: '138%' }}
                  >
                    <Link
                      to={slide.link}
                      onClick={e => handleClickLink(e, slide.pk, slide.slug)}
                      aria-label='product'
                      style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}
                    >
                      <LazyLoadImage
                        alt={spec.shortName}
                        effect='opacity'
                        placeholderSrc={PlaceHolder}
                        aria-label={slide.smallImage}
                        src={(showBigImg ? slide.image : slide.smallImage) || PlaceHolder}
                        visibleByDefault
                      />
                    </Link>
                    {!cardView && showFavoritesButton && product && (
                      <FavoritesBtnView isInFavorites={product.isInFavorites} pk={product.pk} />
                    )}
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
          {cardView && product?.isOutOfStock && (
            // On the image, so it doesn't add height below the price (and isn't dimmed with the out-of-stock card).
            <span className='product-card__badge'>{t('productOutOfStock', 'Out of Stock')}</span>
          )}
          {cardView && (
            // Sits above the slides so the controls don't move with them. Shown on hover (always on touch screens).
            <div className='product-card__overlay'>
              {showFavoritesButton && product && (
                <FavoritesBtnView isInFavorites={product.isInFavorites} pk={product.pk} />
              )}
              {uniqSlides.length > 1 && (
                <>
                  <IconButton
                    className='product-card__arrow'
                    aria-label={t('button.previousImage', 'Previous image')}
                    onClick={() => swiper?.slidePrev()}
                    sx={{ ...arrowSx, left: -4 }}
                  >
                    <ArrowForwardIosIcon viewBox='0 -960 960 960' sx={{ transform: 'rotate(180deg)' }} />
                  </IconButton>
                  <IconButton
                    className='product-card__arrow'
                    aria-label={t('button.nextImage', 'Next image')}
                    onClick={() => swiper?.slideNext()}
                    sx={{ ...arrowSx, right: -4 }}
                  >
                    <ArrowForwardIosIcon viewBox='0 -960 960 960' />
                  </IconButton>
                </>
              )}
              {product && !product.isOutOfStock && (
                <IconButton
                  className='product-card__cart-btn'
                  aria-label={t('button.addToCart', 'Add to Cart')}
                  aria-expanded={isPanelOpen}
                  onClick={openPanel}
                  sx={cartButtonSx}
                >
                  <AddShoppingCartIcon viewBox='0 -960 960 960' />
                </IconButton>
              )}
            </div>
          )}
        </div>
        {/* Below the slider, so it drops Swiper's `z-index: 10`, which put the dots over the sticky filter bar. */}
        <div
          id={`swiper-pagination-${idPrefix}${idx}`}
          className={`swiper-pagination swiper-pagination-clickable min-h-20 h-20 swiper-pagination-bullets w-full mt-5 !pb-0 !z-auto ${
            uniqSlides.length > 0 ? '!block' : 'hidden'
          }`}
        />
      </div>
      <div className={`${product?.isOutOfStock ? 'opacity-50' : ''}`}>
        <div className='caption'>
          <h4 itemProp='name' className='!mt-0'>
            <Link
              aria-label='product'
              className='animsition-link line-clamp-3'
              onClick={e => handleClickLink(e, spec.pk, spec.slug)}
              to={specUrl}
              itemProp='url'
            >
              {activeSlide?.name || spec.shortName || spec.name}
            </Link>
          </h4>
          {/* The card view collapses the price while the quick add-to-cart panel is open. */}
          <div
            ref={priceRef}
            className={cardView ? `product-card__price ${isOpenInCard ? 'product-card__price--collapsed' : ''}` : ''}
          >
            {(activeSlide?.priceRange || priceRange) && (
              <ProductPrice priceRange={activeSlide?.priceRange || priceRange} rangeSeparator={cardView ? '—' : '-'} />
            )}
          </div>
        </div>
        {!cardView && product?.isOutOfStock && (
          <span className='font-bold text-white bg-red px-10 py-5 rounded-full'>Out of Stock</span>
        )}
      </div>
      {cardView && isPanelOpen && !inSheet && (
        <IconButton aria-label={t('close', 'Close')} onClick={() => onTogglePanel?.(false)} sx={closeButtonSx}>
          <CloseIcon viewBox='0 -960 960 960' />
        </IconButton>
      )}
      {cardView && !inSheet && (
        <PanelCollapse
          open={isPanelOpen}
          className='product-card__panel'
          onMeasure={onPanelMeasured}
          onOpened={() => setPanelOpened(true)}
        >
          {panel}
        </PanelCollapse>
      )}
      {cardView && inSheet && (
        <ProductCardSheet
          open={isPanelOpen}
          id={`${idPrefix}${idx}`}
          name={activeSlide?.name || spec.name}
          images={uniqSlides.map(slide => slide.image)}
          activeIndex={activeSlideIndex}
          centered={!isPhone}
          // The card follows, so it shows the same slide (and its product's form) once the sheet closes.
          onActiveIndexChange={index => {
            setActiveSlideIndex(index)
            swiper?.slideToLoop(index, 0)
          }}
          onClose={() => onTogglePanel?.(false)}
        >
          {panel}
        </ProductCardSheet>
      )}
      <ProductDetailsDialog isOpen={!!activeProductData} setIsOpen={setActiveProductData} spec={activeProductData} />
    </>
  )
}

export default ReflectionCard
